// Conversation-bridge shared contracts (conversational-bridge lane, Deliverable B).
// Design law: the LEDGER stores bridge OUTPUT REFERENCES only (binding refs, receipt refs,
// freshness stamps) — never raw evidence rows, never resolved numeric values. The DELTA
// FRAME is an egress artifact and is the only shape here that carries receipt-bound
// NumericBinding values. Everything binds to the generic ObjectiveValueBridgePicture —
// never to CompositeEbitdaPicture or anything under apps/**.
import type { NumericBinding } from '../ask-helix/contracts/numeric-binding';
import type {
  ObjectiveBridgeLeverTier,
  ObjectiveConstraint,
  ObjectiveValueBridgePicture,
} from '../ask-helix/contracts/objective-value-bridge';
import type {
  EvidenceReadinessStatus,
  ObjectiveEvidenceSourceClass,
} from '../objective-to-lever-decomposer';

// ---------------------------------------------------------------------------
// Redaction-safe numeric binding reference
// ---------------------------------------------------------------------------

// Mirrors AskHelixConversationContextFrameDto.activeDecision.numericBindingRefs
// (packages/lib/src/ask-helix.ts) — the frame declares the shape inline, so it is
// re-declared here rather than imported. valueEgress is the structural guarantee:
// a ref can NEVER carry a resolved value.
export type ConversationBridgeNumericBindingRef = Readonly<{
  bindingId: string;
  placeholder?: string | null;
  evidenceId?: string | null;
  claimDomain?: string | null;
  unit?: string | null;
  valueEgress: 'redacted_bound_number';
}>;

// ---------------------------------------------------------------------------
// Dispatch state (per lever)
// ---------------------------------------------------------------------------

// Freshness stamp for the evidence a lever's last run was priced against. Reuse from the
// ledger is a cache with a truth check — stale stamps force a re-run, never a bypass.
export type BridgeLedgerEvidenceFreshnessStamp = Readonly<{
  evidenceId: string;
  governedSnapshotId?: string | null;
  asOf: string;
  sourceClass?: ObjectiveEvidenceSourceClass;
  status: EvidenceReadinessStatus;
}>;

// Typed arg bounds come from DATA in the ledger (copied from the scenario contract at
// dispatch time) — never from module-specific constants in this shared code.
export type BridgeScenarioArgSchema =
  | Readonly<{ kind: 'number'; min?: number; max?: number; unit?: string }>
  | Readonly<{ kind: 'string'; allowedValues?: readonly string[] }>;

export type BridgeLedgerDispatchState = Readonly<{
  // null = priced from the approved snapshot, no scenario wiring — a change_scenario_arg
  // against such a lever must answer honestly, never interpolate.
  scenarioToolId: string | null;
  args: Readonly<Record<string, number | string>>;
  argSchema: Readonly<Record<string, BridgeScenarioArgSchema>>;
  argumentSchemaRef?: string;
  evidenceFreshness: BridgeLedgerEvidenceFreshnessStamp;
}>;

// ---------------------------------------------------------------------------
// Ledger bridge-state ref projection
// ---------------------------------------------------------------------------

// Anchor projection: receipt refs only — the blocked variant names what is missing, the
// evidence-backed variant carries refs, never baseline/target dollar values.
export type LedgerBridgeAnchorRef =
  | Readonly<{
      unit: string;
      asOf: string;
      evidenceRefIds: readonly string[];
      bindingRefs: readonly ConversationBridgeNumericBindingRef[];
    }>
  | Readonly<{
      blockedReason: string;
      missingEvidenceKeys: readonly string[];
    }>;

export type LedgerBridgeLeverRef = Readonly<{
  leverFamilyId: string;
  moduleId: string | null;
  label: string;
  tier: ObjectiveBridgeLeverTier;
  // Absent when the lever's contribution is not publishable as a number.
  contributionBindingRef?: ConversationBridgeNumericBindingRef;
  contributionClaimIds: readonly string[];
  evidenceRefIds: readonly string[];
}>;

export type LedgerBridgeWithheldLeverRef = Readonly<{
  leverFamilyId: string;
  moduleId: string | null;
  label: string;
  // The recorded honest reason — explain_withhold answers from here, read-only.
  withheldReason: string;
  requiredEvidenceKeys: readonly string[];
}>;

// A REF PROJECTION of ObjectiveValueBridgePicture, not the full picture: every numeric
// field is replaced by a ConversationBridgeNumericBindingRef so a compromised session
// store leaks references, not evidence values.
export type LedgerBridgeStateRef = Readonly<{
  generatedAt: string;
  // The picture's objective sub-type is reused as-is: it carries no raw evidence values.
  objective: ObjectiveValueBridgePicture['objective'];
  anchorRef: LedgerBridgeAnchorRef;
  packageRefs: Readonly<{
    approvedPackageValueRef?: ConversationBridgeNumericBindingRef;
    targetFillPctRef?: ConversationBridgeNumericBindingRef;
    remainingGapValueRef?: ConversationBridgeNumericBindingRef;
    canPublishFullCompositeTotal: boolean;
    selectedLeverFamilyIds: readonly string[];
    selectionPolicy: string | null;
  }>;
  leverRefs: readonly LedgerBridgeLeverRef[];
  withheldLeverRefs: readonly LedgerBridgeWithheldLeverRef[];
  // Verdicts + keys + unresolved pairs only — no raw adjustment amounts in the ledger.
  deconfliction: Readonly<{
    status: 'complete' | 'partial' | 'blocked';
    overlapDimensionKeys: readonly string[];
    unresolvedPairs: readonly string[];
  }>;
}>;

// ---------------------------------------------------------------------------
// Conversation bridge ledger
// ---------------------------------------------------------------------------

export const CONVERSATION_BRIDGE_LEDGER_SCHEMA_VERSION =
  'conversation_bridge_ledger_v1';

export type ConversationBridgeLedgerSchemaVersion =
  typeof CONVERSATION_BRIDGE_LEDGER_SCHEMA_VERSION;

export type ConversationBridgeLedger = Readonly<{
  // Fail closed on mismatch: never mutate against a ledger shape you cannot trust.
  schemaVersion: ConversationBridgeLedgerSchemaVersion;
  conversationId: string;
  tenantId: string;
  lastBridge: LedgerBridgeStateRef;
  // Keyed by leverFamilyId: exactly what was dispatched to produce the last bridge.
  dispatchState: Readonly<Record<string, BridgeLedgerDispatchState>>;
  appliedConstraints: readonly ObjectiveConstraint[];
  // APPEND-ONLY — never rewritten. Turn-1 bridge + fold(log[0..k]) replays any state.
  mutationLog: readonly MutationLogEntry[];
  createdAt: string;
  // TTL: past this instant the ledger is untrusted and follow-ups reroute as fresh
  // questions with an honest note.
  expiresAt: string;
}>;

// ---------------------------------------------------------------------------
// Mutation-intent grammar (closed enum)
// ---------------------------------------------------------------------------

// Closed enum — the LLM proposes from these eight kinds, deterministic code disposes.
// Adding a variant is a CONTRACT change, not a prompt change.
export type BridgeMutationIntent =
  | Readonly<{
      kind: 'change_scenario_arg';
      leverFamilyId: string;
      argName: string;
      newValue: number | string;
    }>
  | Readonly<{ kind: 'remove_lever'; leverFamilyId: string }>
  | Readonly<{ kind: 'add_lever'; leverFamilyId: string }>
  | Readonly<{ kind: 'add_constraint'; constraint: ObjectiveConstraint }>
  | Readonly<{ kind: 'remove_constraint'; constraintId: string }>
  | Readonly<{ kind: 'explain_withhold'; leverFamilyId: string }>
  | Readonly<{ kind: 'compare_states'; fromTurn: number; toTurn: number }>
  | Readonly<{ kind: 'reset_to'; turnIndex: number }>;

export type MutationClassification = Readonly<{
  // null = not a mutation; route as a fresh question.
  matched: BridgeMutationIntent | null;
  // 0..1 — below the dispose threshold the resolver clarifies, never guesses.
  confidence: number;
  // Set when ambiguous; asked verbatim.
  clarifyPrompt?: string;
}>;

export type MutationLogEntry = Readonly<{
  turnIndex: number;
  intent: BridgeMutationIntent;
  // Delta summary is REFS only — the log proves what changed without storing values.
  deltaSummary: Readonly<{
    priorPackageValueRef: ConversationBridgeNumericBindingRef;
    newPackageValueRef: ConversationBridgeNumericBindingRef;
    leversChanged: readonly string[];
  }>;
  appliedAt: string;
}>;

// ---------------------------------------------------------------------------
// Delta bridge frame (egress artifact)
// ---------------------------------------------------------------------------

export const DELTA_BRIDGE_FRAME_SCHEMA_VERSION = 'delta_bridge_frame_v1';

export type DeltaBridgeFrameSchemaVersion =
  typeof DELTA_BRIDGE_FRAME_SCHEMA_VERSION;

export type DeltaBridgeLeverRow = Readonly<{
  leverFamilyId: string;
  status: 'changed' | 'added' | 'removed' | 'unchanged' | 'withheld';
  // Receipt-bound or absent — never a naked number.
  priorValue?: NumericBinding;
  nextValue?: NumericBinding;
  // 'reused' is only legal after a freshness check; stale evidence forces a re-run.
  freshness: 'reused' | 'rerun_stale' | 'rerun_changed';
  // Honest note: withheld cause, staleness re-run, constraint drop.
  note?: string;
}>;

// The egress artifact: unlike the ledger, this carries receipt-bound NumericBinding
// values because it flows through the fail-closed SSE redaction like any module output.
export type DeltaBridgeFrame = Readonly<{
  schemaVersion: DeltaBridgeFrameSchemaVersion;
  conversationId: string;
  turnIndex: number;
  mutation: BridgeMutationIntent;
  prior: Readonly<{ packageValue: NumericBinding; gapFillPct: NumericBinding }>;
  next: Readonly<{ packageValue: NumericBinding; gapFillPct: NumericBinding }>;
  leverRows: readonly DeltaBridgeLeverRow[];
}>;
